import { useLiveQuery } from "dexie-react-hooks";
import { useTranslation } from "react-i18next";
import { Play } from "@thuthi/ui/icons";
import { Button } from "@thuthi/ui/components/button";
import { Card } from "@thuthi/ui/components/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@thuthi/ui/components/select";
import { playSound, shuffle, sounds } from "../app/utils";
import session from "../app/Session";
import routes from "./routes";
import accountApi from "../app/AccountApi";
import { Pref, PrefGroup } from "./Pref";
import prefs from "../app/Prefs";
import { UnauthorizedError } from "../app/errors";
import type { Account } from "../app/types";

const maybeUpdateAccountSettings = async (payload: Partial<Account>) => {
  if (!session.exists()) {
    return;
  }
  try {
    await accountApi.updateSettings(payload);
  } catch (e) {
    console.log(`[Preferences] Error updating account settings`, e);
    if (e instanceof UnauthorizedError) {
      await session.resetAndRedirect(routes.login);
    }
  }
};

const Preferences = () => (
  <div className="mx-auto my-6 flex w-full max-w-4xl flex-col gap-6">
    <Notifications />
    <Appearance />
  </div>
);

// 섹션 카드 공통 스타일: shadcn Card 는 기본이 flex+gap 이라 원본(MUI Card)처럼 block + 마진 흐름으로 되돌린다
const sectionCardClass = "block p-6";
const sectionTitleClass = "mb-4 text-xl font-semibold";

const Notifications = () => {
  const { t } = useTranslation();
  return (
    <Card className={sectionCardClass} aria-label={t("prefs_notifications_title")}>
      <h2 className={sectionTitleClass}>{t("prefs_notifications_title")}</h2>
      <PrefGroup>
        <Sound />
        <DeleteAfter />
      </PrefGroup>
    </Card>
  );
};

const Sound = () => {
  const { t } = useTranslation();
  const labelId = "prefSound";
  const sound = useLiveQuery(async () => prefs.sound());
  const handleChange = async (value: string) => {
    await prefs.setSound(value);
    await maybeUpdateAccountSettings({
      notification: {
        sound: value,
      },
    });
  };
  if (!sound) {
    return null; // While loading
  }
  let description;
  if (sound === "none") {
    description = t("prefs_notifications_sound_description_none");
  } else {
    description = t("prefs_notifications_sound_description_some", {
      sound: sounds[sound].label,
    });
  }
  return (
    <Pref labelId={labelId} title={t("prefs_notifications_sound_title")} description={description}>
      <div className="flex w-full items-center gap-1">
        <Select value={sound} onValueChange={handleChange}>
          <SelectTrigger className="min-w-0 flex-1" aria-labelledby={labelId}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t("prefs_notifications_sound_no_sound")}</SelectItem>
            {Object.entries(sounds).map((s) => (
              <SelectItem key={s[0]} value={s[0]}>
                {s[1].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => playSound(sound)}
          disabled={sound === "none"}
          aria-label={t("prefs_notifications_sound_play")}
        >
          <Play />
        </Button>
      </div>
    </Pref>
  );
};

const DeleteAfter = () => {
  const { t } = useTranslation();
  const labelId = "prefDeleteAfter";
  const deleteAfter = useLiveQuery(async () => prefs.deleteAfter());
  const handleChange = async (value: number) => {
    await prefs.setDeleteAfter(value);
    await maybeUpdateAccountSettings({
      notification: {
        delete_after: value,
      },
    });
  };

  if (deleteAfter === null || deleteAfter === undefined) {
    // !deleteAfter will not work with "0"
    return null; // While loading
  }

  const description = (() => {
    switch (deleteAfter) {
      case 0:
        return t("prefs_notifications_delete_after_never_description");
      case 10800:
        return t("prefs_notifications_delete_after_three_hours_description");
      case 86400:
        return t("prefs_notifications_delete_after_one_day_description");
      case 604800:
        return t("prefs_notifications_delete_after_one_week_description");
      case 2592000:
        return t("prefs_notifications_delete_after_one_month_description");
      default:
        return "";
    }
  })();

  return (
    <Pref labelId={labelId} title={t("prefs_notifications_delete_after_title")} description={description}>
      <Select value={String(deleteAfter)} onValueChange={(value) => handleChange(Number(value))}>
        <SelectTrigger className="w-full" aria-labelledby={labelId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="0">{t("prefs_notifications_delete_after_never")}</SelectItem>
          <SelectItem value="10800">{t("prefs_notifications_delete_after_three_hours")}</SelectItem>
          <SelectItem value="86400">{t("prefs_notifications_delete_after_one_day")}</SelectItem>
          <SelectItem value="604800">{t("prefs_notifications_delete_after_one_week")}</SelectItem>
          <SelectItem value="2592000">{t("prefs_notifications_delete_after_one_month")}</SelectItem>
        </SelectContent>
      </Select>
    </Pref>
  );
};

const Appearance = () => {
  const { t } = useTranslation();
  return (
    <Card className={sectionCardClass} aria-label={t("prefs_appearance_title")}>
      <h2 className={sectionTitleClass}>{t("prefs_appearance_title")}</h2>
      <PrefGroup>
        <Language />
      </PrefGroup>
    </Card>
  );
};

const Language = () => {
  const { t, i18n } = useTranslation();
  const labelId = "prefLanguage";
  const lang = i18n.resolvedLanguage ?? "en";

  // Country flags are displayed using emoji. Emoji rendering is handled by platform fonts.
  // Windows in particular does not yet play nicely with flag emoji so for now, hide flags on Windows.
  const randomFlags = shuffle([
    "🇬🇧",
    "🇺🇸",
    "🇪🇸",
    "🇫🇷",
    "🇧🇬",
    "🇨🇿",
    "🇩🇪",
    "🇵🇱",
    "🇺🇦",
    "🇨🇳",
    "🇮🇹",
    "🇭🇺",
    "🇧🇷",
    "🇳🇱",
    "🇮🇩",
    "🇯🇵",
    "🇷🇺",
    "🇹🇷",
    "🇫🇮",
  ]).slice(0, 3);
  const showFlags = !navigator.userAgent.includes("Windows");
  let title = t("prefs_appearance_language_title");
  if (showFlags) {
    title += ` ${randomFlags.join(" ")}`;
  }

  const handleChange = async (value: string) => {
    await i18n.changeLanguage(value);
    await maybeUpdateAccountSettings({
      language: value,
    });
  };

  // Remember: Flags are not languages. Don't put flags next to the language in the list.
  // Languages names from: https://www.omniglot.com/language/names.htm
  // Better: Sidebar in Wikipedia: https://en.wikipedia.org/wiki/Bokm%C3%A5l
  //
  // 이 포크는 en/ko 번역만 제공하므로 목록도 두 개만 남긴다.

  return (
    <Pref labelId={labelId} title={title}>
      <Select value={lang} onValueChange={handleChange}>
        <SelectTrigger className="w-full" aria-labelledby={labelId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="en">English</SelectItem>
          <SelectItem value="ko">한국어</SelectItem>
        </SelectContent>
      </Select>
    </Pref>
  );
};

export default Preferences;
